// Client-side per-page PDF text extraction.
//
// Runs at upload time on the tech's/admin's own device, using the same
// pdfjs-dist build the DocViewer already lazy-loads. The extracted pages ride
// along with the upload (pages_json) so the worker can index them for
// troubleshoot-time retrieval without ever parsing a PDF itself — pdf.js's
// serverless build doesn't initialize under workerd, and the browser build is
// pdf.js's home turf anyway.
//
// Never throws: any failure returns [] and the upload proceeds without pages
// (the worker falls back to the LLM's [PAGE N] transcription, and an admin can
// re-ingest later via POST /api/admin/documents/:id/pages).

export async function extractPdfPages(file) {
  let pdf = null;
  try {
    const buf = await file.arrayBuffer();
    const [pdfjs, workerMod] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default;

    pdf = await pdfjs.getDocument({ data: buf }).promise;
    const pages = [];
    for (let n = 1; n <= pdf.numPages; n++) {
      const page = await pdf.getPage(n);
      const tc = await page.getTextContent();
      // Rebuild rough line structure from pdf.js text items: an item that ends
      // a line carries hasEOL. The worker's boilerplate stripper works on
      // lines, so keeping them matters.
      let text = '';
      for (const item of tc.items) {
        text += item.str;
        text += item.hasEOL ? '\n' : ' ';
      }
      const content = text.replace(/[ \t]+/g, ' ').trim();
      if (content) pages.push({ page_number: n, content: content.slice(0, 20000) });
      page.cleanup?.();
    }
    return pages;
  } catch (err) {
    console.warn('client PDF text extraction failed (upload proceeds without pages):', err);
    return [];
  } finally {
    pdf?.destroy?.();
  }
}


// Render PDF pages to JPEG blobs, one at a time, calling onPage(n, blob,
// total) for each. Runs after the upload so the tech is never blocked; the
// worker stores each image and OCRs pages that had no text layer. Capped —
// a 400-page parts catalogue is not worth 400 uploads from a truck.
export const MAX_PAGE_IMAGES = 120;
export async function renderPdfPageImages(file, onPage, { maxPages = MAX_PAGE_IMAGES, maxWidth = 1400, quality = 0.82, skip = new Set() } = {}) {
  let pdf = null;
  try {
    const buf = await file.arrayBuffer();
    const [pdfjs, workerMod] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerMod.default;
    pdf = await pdfjs.getDocument({ data: buf }).promise;
    const total = Math.min(pdf.numPages, maxPages);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    for (let n = 1; n <= total; n++) {
      if (skip.has(n)) continue;
      const page = await pdf.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const scale = Math.min(2, maxWidth / base.width);
      const viewport = page.getViewport({ scale });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // intent 'print': pdf.js paces DISPLAY renders with requestAnimationFrame,
      // which a background/hidden tab never delivers — the render promise then
      // simply never resolves (measured: hung indefinitely in a hidden tab; the
      // same page rendered in 1.2 s with print intent). A tech who switches
      // apps mid-index must not stall the whole run.
      await page.render({ canvasContext: ctx, viewport, intent: 'print' }).promise;
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', quality));
      page.cleanup?.();
      if (blob) await onPage(n, blob, total);
    }
    return total;
  } catch (err) {
    console.warn('page image rendering failed (non-fatal):', err);
    return 0;
  } finally {
    pdf?.destroy?.();
  }
}
